/**
 * Where enquiries are read from and written to — and what the browser still
 * holds on top.
 *
 * Three endpoints: `GET /api/v1/inquiries/` for the list, paginated and
 * searched by the server, `GET /api/v1/inquiries/{id}/` for one whole enquiry,
 * and `POST /api/v1/inquiries/add/` to raise one. Everything snake_case stops
 * here; the rest of the module sees `Enquiry`, `EnquiryRecord` and the app's
 * own casing.
 *
 * A fourth raises money against one: `POST /api/v1/inquiries/{id}/payment-
 * requests/`, which answers with the invoice the backend generated. The detail
 * response now carries that invoice back as its `payment_request`, so a
 * requested payment is read from the server like every other field of the
 * record and survives a reload — see `toRecord` below.
 *
 * Moving an enquiry's stage is a real call too: the same `PATCH
 * /api/v1/inquiries/update/{id}/` the edit form uses, sent with `status` alone.
 *
 * The store below is no longer where a stage lives, then — it is what keeps the
 * screens agreeing between fetches. It holds an override per enquiry id, rather
 * than a whole record, because a screen changing a stage has the row's id and
 * nothing else, and what goes in has already been saved. It lasts as long as
 * the tab does; a reload asks the server, which now answers with the same
 * thing. A payment goes in beside the stage so the screen that raised or
 * settled one shows it without a refetch.
 */

import {
  api,
  API_ENDPOINTS,
  isPaymentRequestCancelled,
  isPaymentRequestPaid,
  paymentPlanName,
  paymentPlanPrice,
  toAmountDue,
  toAmountNumber,
  toLookupReferences,
  withValidationDetail,
} from "../../../lib/api";
import type {
  CreatePaymentRequestPayload,
  PaginatedResponse,
  PaymentRequestResponse,
} from "../../../lib/api";
import { toNationalNumber } from "../../../lib/fieldFormat";
// The same rule client records are written by: `MALE` reads as "Male".
import { formatEnumLabel } from "../../clients/clientMapping";
import type { GetToken } from "../../auth/types/auth.types";
import {
  cancelPaymentRequest,
  confirmPaymentRequest,
} from "../../payments/services/paymentRequestsService";
import {
  daysAgoLabel,
  daysAgoOf,
  formatShortDate,
  fullNameOf,
  toEnquiryMode,
  toEnquiryStatus,
  toEnquiryStatusCode,
} from "../enquiryMapping";
import type {
  CreateEnquiryPayload,
  Enquiry,
  EnquiryDetailResponse,
  EnquiryPage,
  EnquiryPayment,
  EnquiryPaymentStatus,
  EnquiryRecord,
  EnquiryStatus,
  EnquirySummaryResponse,
  NewEnquiryPayment,
} from "../types/enquiries.types";

/** The largest page the endpoint will serve. Anything above is clamped to it. */
export const MAX_PAGE_SIZE = 100;

/**
 * What a list call asks for unless it says otherwise. Ten, matching the ten
 * rows the table shows, so a page of the API is a page of the table.
 */
export const DEFAULT_PAGE_SIZE = 10;

/**
 * Every goal on a response, named — `goals` now, `goal` on a record the backend
 * has not migrated, and either one object or a list of them. See
 * `toLookupReferences`, which is where that tolerance is spelled out.
 */
const goalNamesOf = (
  response: Pick<EnquirySummaryResponse, "goals" | "goal">
): string[] =>
  toLookupReferences(response.goals ?? response.goal)
    .map((goal) => goal.name.trim())
    .filter(Boolean);

/**
 * The word the pill shows: the backend's own `status_display` where the
 * response carries one — "Onboarded" for a `CONVERTED` enquiry, not the
 * "Converted" the code alone would read as — and the app's own label from
 * `toEnquiryStatus` where it does not, so a response an older backend sent
 * still draws a word.
 */
const statusLabelOf = (
  response: Pick<EnquirySummaryResponse, "status" | "status_display">
): string => response.status_display?.trim() || toEnquiryStatus(response.status);

/**
 * One row, in the shape the table reads.
 *
 * Every field is defended: an enquiry with no goal or no name is a row the
 * list still has to render, and a dash in a cell is a better answer than a
 * screen that fails to draw.
 *
 * The payment is taken where the list sends one and left off where it does not
 * — the table answers a request from the row, and knowing which requests are
 * still open is what tells a tick from a pill. A row without one is not a row
 * without a request; see `Enquiry`.
 */
const toRow = (response: EnquirySummaryResponse): Enquiry => ({
  id: response.public_id,
  name: fullNameOf({
    firstName: response.first_name ?? "",
    lastName: response.last_name ?? "",
  }),
  goals: goalNamesOf(response),
  mode: toEnquiryMode(response.enquiry_source),
  requestedOn: formatShortDate(response.created_at),
  // The server counts the days; a response that left the field out is counted
  // here instead rather than leaving the column blank.
  daysAgo:
    typeof response.days_ago === "number"
      ? daysAgoLabel(response.days_ago)
      : daysAgoOf(response.created_at),
  status: toEnquiryStatus(response.status),
  statusLabel: statusLabelOf(response),
  payment: response.payment_request
    ? toEnquiryPayment(response.payment_request)
    : undefined,
});

/**
 * Where the request stands, in this feature's three words. Both questions are
 * the API layer's to answer — see `isPaymentRequestPaid` — so a backend state
 * the app has never seen reads as `Pending`, a question still open, rather than
 * as settled or as a blank pill.
 */
const toPaymentStatus = (
  response: PaymentRequestResponse
): EnquiryPaymentStatus => {
  if (isPaymentRequestPaid(response)) return "Paid";
  if (isPaymentRequestCancelled(response)) return "Cancelled";
  return "Pending";
};

/**
 * The invoice the backend holds, as the enquiry screens hold one — whether it
 * arrived on the `POST` that raised it or embedded in the enquiry it was raised
 * against.
 *
 * `planId` is the one field of neither response: the invoice names the
 * plan rather than pointing at it. The raise passes it through from what was
 * asked, which is the one place it is known; a request read back off an enquiry
 * has none, and nothing needs one — see `EnquiryPayment`.
 *
 * Every field is defended: a request the backend sent with a plan it could
 * not name is still a row the screen has to draw, and a dash is a better answer
 * than a screen that fails to render.
 */
const toEnquiryPayment = (
  response: PaymentRequestResponse,
  planId?: string
): EnquiryPayment => ({
  id: response.public_id,
  invoiceNumber: response.invoice_number ?? "",
  planId,
  planName: paymentPlanName(response) || "-",
  duration: response.duration_label || "-",
  price: toAmountNumber(paymentPlanPrice(response)),
  amount: toAmountNumber(response.amount_due),
  requestedAt: response.generated_on,
  status: toPaymentStatus(response),
  // `paid_on` is the day; the receipt carries it too, and is what a request
  // that was settled without the top-level field being set still reads from.
  receivedOn: response.paid_on ?? response.payment?.received_on ?? null,
});

/**
 * One whole enquiry, as the app holds one.
 *
 * The reference fields arrive as `{ id, name }` objects and are kept as their
 * names: an enquiry is only ever displayed, never posted back, so the ids
 * would be carried for nothing. They join the record the day there is an
 * endpoint to save one to.
 *
 * The payment is the exception: it arrives embedded whole, and is mapped as the
 * record it is. A response carrying none leaves the field off, which is what
 * the detail screen reads as "nothing has been asked for yet".
 */
const toRecord = (response: EnquiryDetailResponse): EnquiryRecord => ({
  id: response.public_id,
  title: response.title?.name ?? "",
  firstName: response.first_name ?? "",
  lastName: response.last_name ?? "",
  gender: formatEnumLabel(response.gender ?? ""),
  dateOfBirth: response.date_of_birth ?? "",
  profession: response.profession ?? "",
  lifestyle: response.life_style?.name ?? "",
  goals: goalNamesOf(response),
  mode: toEnquiryMode(response.enquiry_source),
  fullAddress: response.full_address ?? "",
  state: response.state ?? "",
  city: response.city ?? "",
  postalCode: response.postal_code ?? "",
  phone: toNationalNumber(response.phone_number ?? ""),
  whatsapp: toNationalNumber(response.whatsapp_number ?? ""),
  email: response.email ?? "",
  status: toEnquiryStatus(response.status),
  statusLabel: statusLabelOf(response),
  createdAt: response.created_at,
  daysAgo: response.days_ago,
  payment: response.payment_request
    ? toEnquiryPayment(response.payment_request)
    : undefined,
});

export interface FetchEnquiriesOptions {
  /** 1-based, the way the endpoint counts. */
  page?: number;
  /**
   * Defaults to `DEFAULT_PAGE_SIZE`, and is capped at `MAX_PAGE_SIZE` — all
   * the backend will serve.
   */
  pageSize?: number;
  /**
   * Matched by the server against the enquirer's name. Blank or whitespace is
   * left off rather than sent, so an empty search box asks for the unfiltered
   * list.
   */
  search?: string;
  signal?: AbortSignal;
}

/** One page of enquiries, newest first. */
export async function fetchEnquiriesPage(
  getToken: GetToken,
  { page, pageSize = DEFAULT_PAGE_SIZE, search, signal }: FetchEnquiriesOptions = {}
): Promise<EnquiryPage> {
  const token = await getToken();
  if (!token) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error("No active Clerk session - cannot load enquiries.");
  }

  const response = await api.get<PaginatedResponse<EnquirySummaryResponse>>(
    API_ENDPOINTS.enquiries.list,
    {
      token,
      // `undefined` entries are dropped by the API client, so an empty search
      // never reaches the URL.
      query: {
        page,
        page_size: Math.min(pageSize, MAX_PAGE_SIZE),
        search: search?.trim() || undefined,
      },
      signal,
    }
  );

  return {
    count: response?.count ?? 0,
    hasNext: !!response?.next,
    enquiries: (response?.results ?? []).map(toRow),
  };
}

/**
 * One enquiry, by the `public_id` a list row carries.
 *
 * A `public_id` that is not the caller's, or not an enquiry at all, comes back
 * 404. That reaches the caller as an `ApiError` carrying the status rather
 * than as an empty record — `useEnquiry` branches on it to tell a missing
 * enquiry from a failed request.
 */
export async function fetchEnquiry(
  getToken: GetToken,
  publicId: string,
  signal?: AbortSignal
): Promise<EnquiryRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot load an enquiry.");
  }

  const response = await api.get<EnquiryDetailResponse>(
    API_ENDPOINTS.enquiries.detail(publicId),
    { token, signal }
  );

  return toRecord(response);
}

/**
 * Raises an enquiry and returns the record the backend saved, so the caller
 * can navigate straight to it. It comes back as a whole enquiry — its
 * `public_id`, and the `JUST_ADDED` the backend opens every one at — so the
 * response maps like any other.
 *
 * JSON, the plain payload `enquiryPayload` produces sent as-is — `goals` goes
 * up as a real array, `goals: ["b1f2c3d4-…", "c2e3d4f5-…"]`, rather than a
 * repeated form field.
 *
 * A 400 arrives as an `ApiError` whose message names the fields the backend
 * rejected rather than just "Validation failed"; a 404 means one of the ids
 * was not in its list, which is a catalog that moved under an open form.
 */
export async function createEnquiry(
  getToken: GetToken,
  payload: CreateEnquiryPayload,
  signal?: AbortSignal
): Promise<EnquiryRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot add an enquiry.");
  }

  try {
    const response = await api.post<EnquiryDetailResponse>(
      API_ENDPOINTS.enquiries.create,
      payload,
      { token, signal }
    );

    return toRecord(response);
  } catch (error) {
    throw withValidationDetail(error);
  }
}

/**
 * Edits one, and returns the record as the backend now holds it — so the
 * screen that opens next reads what was stored rather than what was typed.
 *
 * JSON, the same shape and the same fields `create` takes: a `PATCH` in name,
 * but every field goes up each time, because the form collects all of them
 * whether or not they were touched.
 *
 * The response is a detail record bar its `days_ago`, which this call does not
 * send back — see `EnquiryDetailResponse`.
 */
export async function updateEnquiry(
  getToken: GetToken,
  publicId: string,
  payload: CreateEnquiryPayload,
  signal?: AbortSignal
): Promise<EnquiryRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot update an enquiry.");
  }

  try {
    const response = await api.patch<EnquiryDetailResponse>(
      API_ENDPOINTS.enquiries.update(publicId),
      payload,
      { token, signal }
    );

    return toRecord(response);
  } catch (error) {
    throw withValidationDetail(error);
  }
}

/**
 * Moves an enquiry to another stage of the pipeline — what the Change status
 * dialog saves, wherever it was opened from.
 *
 * The same endpoint the edit form patches, sent with the one field: a JSON
 * body carrying `status` and nothing else, so no other field of the enquiry is
 * touched by a stage being moved. The word on screen goes up as the constant
 * the backend keeps — "Not interested" as `NOT_INTERESTED`; see
 * `toEnquiryStatusCode`.
 *
 * What comes back is the whole enquiry as the backend now holds it, so the
 * stage the caller shows is the stored one rather than the one that was picked.
 * It goes into the store on the way through — only the stage, since that is all
 * this call moved — which is what lets the list reflect a change made on the
 * detail screen, and the other way round, without either refetching.
 *
 * A 400 arrives as an `ApiError` naming the field the backend rejected; a 404
 * means the enquiry is not there any more, which is a row that went stale under
 * an open dialog.
 */
export async function updateEnquiryStatus(
  getToken: GetToken,
  publicId: string,
  status: EnquiryStatus,
  signal?: AbortSignal
): Promise<EnquiryRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot update a status.");
  }

  try {
    const response = await api.patch<EnquiryDetailResponse>(
      API_ENDPOINTS.enquiries.update(publicId),
      { status: toEnquiryStatusCode(status) },
      { token, signal }
    );

    const record = toRecord(response);
    patchOverride(publicId, {
      status: record.status,
      statusLabel: record.statusLabel,
    });
    return record;
  } catch (error) {
    throw withValidationDetail(error);
  }
}

/**
 * Asks an enquiry for money, and returns the invoice the backend generated —
 * its number, its date, and the discount the amount worked out to, none of
 * which the dialog knew before it asked.
 *
 * What it answers with also goes into the store below, so the screen that asked
 * shows the invoice without going back for the enquiry. That copy is a
 * shortcut rather than the record: the same request comes back on the enquiry's
 * own detail response from here on, which is what a reload reads.
 *
 * The enquiry moves to "Payment sent" with it — the two are one act, so nothing
 * can leave a request raised against an enquiry that still reads as unworked.
 * The stage is written into the store here rather than patched over the wire:
 * raising the request is what moves it on the backend too.
 *
 * A 400 arrives as an `ApiError` whose message names the fields the backend
 * rejected rather than just "Validation failed"; a 404 means the plan id
 * was not one of the workspace's, which is a catalog that moved under an open
 * dialog.
 */
export async function requestEnquiryPayment(
  getToken: GetToken,
  enquiryId: string,
  input: NewEnquiryPayment,
  signal?: AbortSignal
): Promise<EnquiryPayment> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot request a payment.");
  }

  const body: CreatePaymentRequestPayload = {
    package: input.planId,
    amount_due: toAmountDue(input.amount),
  };

  let response: PaymentRequestResponse;
  try {
    response = await api.post<PaymentRequestResponse>(
      API_ENDPOINTS.enquiries.paymentRequests(enquiryId),
      body,
      { token, signal }
    );
  } catch (error) {
    throw withValidationDetail(error);
  }

  const payment = toEnquiryPayment(response, input.planId);
  patchOverride(enquiryId, {
    payment,
    status: "Payment sent",
    statusLabel: "Payment sent",
  });
  return payment;
}

/* ── What the screens keep between fetches ─────────────────────────────────
 * Everything below is the browser's copy of changes the server already has.
 * See the note at the top.
 */

/**
 * A change made here to an enquiry the server also holds. Only the two fields
 * a screen can move: a stage, and the payment raised against it.
 */
export interface EnquiryOverride {
  status?: EnquiryStatus;
  /** The label beside `status` above — see `Enquiry.statusLabel`. */
  statusLabel?: string;
  payment?: EnquiryPayment;
}

/** Changes made in this sitting, by enquiry id. */
let overrides: Record<string, EnquiryOverride> = {};

const listeners = new Set<() => void>();

const publish = () => listeners.forEach((listener) => listener());

/**
 * Subscribes to every change made here, and returns the unsubscribe.
 * `useSyncExternalStore` is what calls this — see `useEnquiries`.
 */
export const subscribeToEnquiries = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * The changes made here, to lay over whatever the server returned.
 *
 * The reference only changes when something below replaces it, which is what
 * lets `useSyncExternalStore` use it as a snapshot: a fresh object per call
 * would be an infinite re-render.
 */
export const getEnquiryOverrides = (): Record<string, EnquiryOverride> =>
  overrides;

/** Writes one field of an override without disturbing the other. */
const patchOverride = (id: string, patch: EnquiryOverride): void => {
  overrides = { ...overrides, [id]: { ...overrides[id], ...patch } };
  publish();
};

/**
 * Keeps a payment read back off the server beside the row it belongs to.
 *
 * For the list, which shows a tick and a cross against every enquiry standing
 * at "Payment sent" and has to fetch the request behind one before it can ask
 * about it. Where that read comes back already settled — answered on another
 * screen, or in another tab — this is what puts the row right: the icons give
 * way to a pill without the table having to be reloaded to learn it.
 *
 * Nothing is being changed here, which is why it does not go near the network:
 * the server is where this came from.
 */
export const noteEnquiryPayment = (
  id: string,
  payment: EnquiryPayment
): void => patchOverride(id, { payment });

/**
 * Forgets the stage this browser was holding for one enquiry, leaving whatever
 * else the override carries.
 *
 * For the moves the backend makes on its own. Settling a payment is one: the
 * confirm call says nothing about a stage, but the enquiry may well be at
 * another one afterwards, and the "Payment sent" written here when the request
 * went out would sit on top of it and hide the move. Dropping it first is what
 * lets a re-read of the enquiry show the stage the server actually holds.
 *
 * The label goes with the stage: the two are written together, and a pill reads
 * the label, so a label left behind would go on saying "Payment sent" over the
 * "Onboarded" the re-read brought back — which is the very thing this exists
 * to let through.
 *
 * A no-op where nothing was being held, and the reference is left alone in that
 * case — `useSyncExternalStore` reads it as a snapshot.
 */
export const clearEnquiryStatusOverride = (id: string): void => {
  const held = overrides[id];
  if (!held || (held.status === undefined && held.statusLabel === undefined)) {
    return;
  }

  const { status, statusLabel, ...rest } = held;
  overrides = { ...overrides, [id]: rest };
  publish();
};

/**
 * Marks a requested payment as received, on the day it came in — the detail
 * screen's Confirm payment.
 *
 * The request is settled through its own `public_id` rather than the enquiry's:
 * once raised it is a record of its own, and this is the same call a client's
 * payment history makes. What comes back is the receipt, which is folded onto
 * the payment and kept beside the stage — so the screen turns green on the
 * strength of what was stored, without going back for the enquiry.
 *
 * The payment is the caller's to pass rather than something looked up here: the
 * screen is showing one, and it may well have come from the server rather than
 * from anything this module put in the store.
 *
 * Nothing reports the money in, which is why there is a date to pass at all:
 * the day it arrived is the dietitian's to state.
 */
export async function confirmEnquiryPayment(
  getToken: GetToken,
  enquiryId: string,
  payment: EnquiryPayment,
  receivedOn: string,
  signal?: AbortSignal
): Promise<EnquiryPayment> {
  if (!payment?.id) {
    // Nothing to settle, and no call worth making: the screen only offers this
    // against a request it is already showing.
    throw new Error("No payment request to confirm for this enquiry.");
  }

  const receipt = await confirmPaymentRequest(
    getToken,
    payment.id,
    receivedOn,
    signal
  );

  const paid: EnquiryPayment = {
    ...payment,
    status: "Paid",
    receivedOn: receipt.receivedOn,
  };

  patchOverride(enquiryId, { payment: paid });
  return paid;
}

/**
 * Calls a raised request off — the detail screen's Payment declined.
 *
 * The other half of the question `confirmEnquiryPayment` asks, and settled the
 * same way: through the request's own `public_id`, by the call a client's
 * payment history makes, with what comes back kept beside the stage so the
 * screen greys the amount without going back for the enquiry.
 *
 * Only the status is folded on. The response carries the moment it was called
 * off, but an enquiry shows one payment rather than a history of them, so there
 * is nowhere on this screen for a cancellation date to be read — a client's row
 * keeps that, and rebuilds itself from the response for it.
 *
 * The enquiry's own stage is left where it is. A declined payment is not a
 * decision about the person: they may pay by another means, or be asked again,
 * and moving them off "Payment sent" from here would overwrite a stage the
 * dietitian may have set by hand.
 */
export async function cancelEnquiryPayment(
  getToken: GetToken,
  enquiryId: string,
  payment: EnquiryPayment,
  signal?: AbortSignal
): Promise<EnquiryPayment> {
  if (!payment?.id) {
    // Nothing to call off, and no call worth making — see the confirm above.
    throw new Error("No payment request to cancel for this enquiry.");
  }

  await cancelPaymentRequest(getToken, payment.id, signal);

  const cancelled: EnquiryPayment = { ...payment, status: "Cancelled" };
  patchOverride(enquiryId, { payment: cancelled });
  return cancelled;
}

/**
 * Empties everything this module holds.
 *
 * Test-only. The store is a module singleton, so without it a stage one test
 * moves is still moved for the next one. Nothing in the app calls this: a
 * reload is what clears it there.
 */
export const resetEnquiriesStore = (): void => {
  overrides = {};
  publish();
};
